import { Injectable } from '@angular/core';
import { SyllabusQuery } from '@examdojo/category/v2';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { distinctUntilDeepChanged } from '@examdojo/core/rxjs';
import { debounceTime, EMPTY, finalize, first, map, merge, of, switchMap, tap } from 'rxjs';
import { QuestionBankHttpService } from '../data/question-bank-http.service';
import { mapQuestionBankQuestionHttpModelToStoreModel } from '../data/question-bank-question.mapper';
import { QuestionBankTagsService } from '../data/question-bank-tags.service';
import { QuestionBankHttpModel } from '../data/quetion-bank-http.model';
import { MarksRange, Pagination, QuestionBankFilters, QuestionBankSortOption } from './question-bank.model';
import { INIT_QUESTION_BANK_FILTERS, INIT_QUESTION_BANK_PAGINATION, QuestionBankStore } from './question-bank.store';

@Injectable()
export class QuestionBankService {
  constructor(
    private readonly questionBankHttpService: QuestionBankHttpService,
    private readonly questionBankTagsService: QuestionBankTagsService,
    private readonly store: QuestionBankStore,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly syllabusQuery: SyllabusQuery,
  ) {}

  init(classId?: string) {
    this.store.reset();
    this.store.setLoading(true);

    return this.syllabusQuery.activeIdAndCourseLevel$.pipe(
      first(Boolean),
      switchMap(({ syllabusId, courseLevel }) => {
        this.store.updateProps(() => ({
          syllabusId,
          courseLevel,
          ...(classId ? { classId } : {}),
        }));
        return merge(this.loadMarksRange(syllabusId), this.questionBankTagsService.load(), this.loadFiltersListener());
      }),
    );
  }

  changeFilters(filters: Partial<QuestionBankFilters>) {
    this.store.updateProps((props) => {
      const normalizedFilters = this.normalizeFilters({ ...props.filters, ...filters }, props.marksRange);

      return {
        filters: normalizedFilters,
        pagination: { ...props.pagination, page: 1 },
      };
    });
  }

  changeSort(sortBy: QuestionBankSortOption) {
    this.store.updateProp('sortBy', sortBy);
  }

  clearFilters() {
    this.store.updateProps(() => ({
      filters: INIT_QUESTION_BANK_FILTERS,
      pagination: INIT_QUESTION_BANK_PAGINATION,
    }));
  }

  reload() {
    const { syllabusId, classId, courseLevel, filters, pagination, sortBy } = this.store.getValue().props;
    if (syllabusId === 0) {
      return EMPTY;
    }
    this.store.setLoading(true);
    return this.loadQuestions(syllabusId, filters, sortBy, pagination, classId, courseLevel);
  }

  changePage(page: number) {
    // TODO: [FE-1949] Extract paginagion plug and play component
    const currentState = this.store.getValue();
    if (page < 1) {
      return;
    }
    const totalPages = Math.ceil(currentState.props.total / currentState.props.pagination.pageSize);
    if (page > totalPages) {
      return;
    }
    this.store.updateProp('pagination', { ...currentState.props.pagination, page });
  }

  previousPage() {
    const currentPagination = this.store.getValue().props.pagination;
    const currentPage = currentPagination.page;
    if (currentPage <= 1) {
      return;
    }
    this.store.updateProp('pagination', { ...currentPagination, page: currentPage - 1 });
  }

  nextPage(): void {
    const currentState = this.store.getValue();
    const totalPages = Math.ceil(currentState.props.total / currentState.props.pagination.pageSize);
    if (currentState.props.pagination.page >= totalPages) {
      return;
    }
    this.store.updateProp('pagination', {
      ...currentState.props.pagination,
      page: currentState.props.pagination.page + 1,
    });
  }

  private loadFiltersListener() {
    return this.store.pipe(
      map((state) => ({
        syllabusId: state.props.syllabusId,
        classId: state.props.classId,
        courseLevel: state.props.courseLevel,
        filters: state.props.filters,
        pagination: state.props.pagination,
        sortBy: state.props.sortBy,
      })),
      distinctUntilDeepChanged(),
      tap(() => this.store.setLoading(true)), // Set loading  is set before debounce for better UX
      debounceTime(500),
      switchMap(({ syllabusId, classId, courseLevel, filters, pagination, sortBy }) => {
        if (syllabusId === 0) {
          return of(null).pipe(tap(() => this.store.resetLoading()));
        }
        return this.loadQuestions(syllabusId, filters, sortBy, pagination, classId, courseLevel);
      }),
    );
  }

  private loadQuestions(
    syllabusId: number,
    filters: QuestionBankFilters,
    sortBy: QuestionBankSortOption,
    pagination: Pagination,
    classId?: string,
    courseLevel?: string | null,
  ) {
    return this.questionBankHttpService.getAll(syllabusId, filters, sortBy, pagination, classId, courseLevel).pipe(
      this.errorHandlerService.catchHttpErrors(() => EMPTY),
      tap((response: QuestionBankHttpModel) => {
        this.store.set(response.questions.map(mapQuestionBankQuestionHttpModelToStoreModel));
        this.store.updateProp('total', response.total);
        this.store.updateProp('facets', response.facets);
        this.store.updateProp('questionTagMap', response.questionTagMap);
      }),
      finalize(() => this.store.resetLoading()),
    );
  }

  private loadMarksRange(syllabusId: number) {
    return this.questionBankHttpService.getMarksRange(syllabusId).pipe(
      this.errorHandlerService.catchError('[QuestionBankService]: Failed to load marks range', () => EMPTY),
      tap((marksRange) => {
        this.store.updateProp('marksRange', marksRange);
      }),
    );
  }

  private normalizeFilters(
    filters: Partial<QuestionBankFilters>,
    marksRange: MarksRange,
  ): Partial<QuestionBankFilters> {
    const hasMinPoints = 'minPoints' in filters;
    const hasMaxPoints = 'maxPoints' in filters;
    const hasMinLatestScore = 'minLatestScore' in filters;
    const hasMaxLatestScore = 'maxLatestScore' in filters;
    const hasLastAttemptDateFrom = 'lastAttemptDateFrom' in filters;
    const hasLastAttemptDateTo = 'lastAttemptDateTo' in filters;
    const isNotCompleted = filters.completed === false;

    return {
      ...filters,
      ...(hasMinPoints ? { minPoints: filters.minPoints === marksRange.min ? undefined : filters.minPoints } : {}),
      ...(hasMaxPoints ? { maxPoints: filters.maxPoints === marksRange.max ? undefined : filters.maxPoints } : {}),
      ...(hasMinLatestScore
        ? { minLatestScore: filters.minLatestScore === 0 || isNotCompleted ? undefined : filters.minLatestScore }
        : {}),
      ...(hasMaxLatestScore
        ? { maxLatestScore: filters.maxLatestScore === 100 || isNotCompleted ? undefined : filters.maxLatestScore }
        : {}),
      ...(hasLastAttemptDateFrom
        ? { lastAttemptDateFrom: isNotCompleted ? undefined : filters.lastAttemptDateFrom }
        : {}),
      ...(hasLastAttemptDateTo ? { lastAttemptDateTo: isNotCompleted ? undefined : filters.lastAttemptDateTo } : {}),
    };
  }
}
